import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Check, ChevronsUpDown, MapPin } from 'lucide-react';

import { BLUE, BLUE_D, INK, MUTE, FAINT, LINE, WORDMARK_BLUE } from '@/styles/milk';
import { COUNTRIES, regionForCountry, REGION_LABELS, countryName } from '@/data/countries';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { useAuth } from '@/contexts/AuthContext';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Best-effort country guess from the browser locale (e.g. "en-CA" -> "CA").
function detectCountry() {
  try {
    const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
    for (const l of langs) {
      const m = (l || '').match(/[-_]([A-Za-z]{2})(?![A-Za-z])/);  // region subtag, ignore POSIX suffixes
      const region = m && m[1].toUpperCase();
      if (region && COUNTRIES.some(c => c.code === region)) return region;
    }
  } catch { /* ignore */ }
  return null;
}

// ─── Step Definitions ────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 'country',
    label: 'WHERE YOU WORK',
    heading: 'Which country are\nyou based in?',
    sub: 'We store your data in the region closest to you for privacy and compliance. This is set once and cannot be changed later.',
    type: 'country',
    key: 'country',
  },
  {
    id: 'vertical',
    label: "LET'S GET STARTED",
    heading: 'What best describes\nyour work?',
    sub: 'This helps Milk personalise your Action Cards from day one.',
    type: 'single',
    key: 'vertical',
    options: [
      { value: 'contractor', label: 'Contractor / Trades' },
      { value: 'real_estate', label: 'Real Estate Agent' },
      { value: 'financial', label: 'Financial Advisor / Insurance' },
      { value: 'general', label: 'General' },
      { value: 'other', label: 'Other' },
    ],
  },
  {
    id: 'workflow',
    label: 'YOUR WORKFLOW',
    heading: 'How do you typically\nwork?',
    sub: 'Select all that apply. This shapes which actions Milk suggests.',
    type: 'multi',
    key: 'workflow_types',
    options: {
      contractor: ['I do site visits', 'I send quotes', 'I manage jobs', 'I invoice clients', 'I coordinate subcontractors', 'I handle client calls'],
      real_estate: ['I show properties', 'I manage leads', 'I send listings', 'I follow up with buyers', 'I negotiate offers', 'I coordinate closings'],
      financial: ['I meet clients', 'I recommend products', 'I send follow-ups', 'I track compliance', 'I prepare reviews', 'I handle enquiries'],
      general: ['I run client meetings', 'I follow up on proposals', 'I manage projects', 'I coordinate teams', 'I handle sales calls', 'I track deliverables'],
    },
  },
  {
    id: 'tone',
    label: 'YOUR VOICE',
    heading: 'How should your\ncommunication sound?',
    sub: 'Pick one or more — Milk blends them when writing emails and summaries.',
    type: 'multiTone',
    key: 'communication_style',
    options: [
      { value: 'professional', label: 'Professional', desc: 'Formal and polished' },
      { value: 'friendly', label: 'Friendly', desc: 'Warm and approachable' },
      { value: 'direct', label: 'Direct', desc: 'Clear and to the point' },
      { value: 'brief', label: 'Brief & Concise', desc: 'Short, no fluff' },
    ],
  },
  {
    id: 'preferences',
    label: 'YOUR PREFERENCES',
    heading: 'How should Milk\nhandle your work?',
    sub: 'Set your defaults — you can always change these in Settings.',
    type: 'preferences',
    subSteps: [
      { key: 'follow_up_timing', question: 'Default follow-up timing', options: [{ value: 'same_day', label: 'Same day' }, { value: 'next_day', label: 'Next day' }, { value: '48_hours', label: '48 hours' }] },
      { key: 'auto_create_tasks', question: 'Task creation', options: [{ value: true, label: 'Auto-create tasks' }, { value: false, label: 'Ask me first' }] },
      { key: 'execution_mode', question: 'Email mode', options: [{ value: 'draft', label: 'Draft only' }, { value: 'draft_send', label: 'Draft + prompt to send' }] },
    ],
  },
  {
    id: 'ready',
    label: "YOU'RE ALL SET",
    heading: "Let's create your\nfirst Action Card",
    sub: 'Your AI is now personalised and ready to go.',
    type: 'cta',
    options: [
      { value: 'paste', label: 'Paste notes', desc: 'From a call or meeting' },
      { value: 'upload', label: 'Upload audio', desc: 'Record or existing file' },
      { value: 'meeting', label: 'Record a meeting', desc: 'Send a bot to your Zoom / Meet / Teams call' },
      { value: 'sample', label: 'Take me to my dashboard', desc: 'Explore first' },
    ],
  },
];

function Pill({ label, desc, selected, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="rounded-full text-sm font-bold border text-left flex items-center gap-2"
      style={{
        padding: '11px 18px',
        background: selected ? BLUE : '#FFFFFF',
        color: selected ? '#FFFFFF' : INK,
        borderColor: selected ? BLUE : LINE,
        boxShadow: selected ? '0 6px 16px rgba(103,150,194,0.30)' : 'none',
        transition: 'background .15s, border-color .15s, transform .15s',
      }}
    >
      {selected && <Check size={13} style={{ flexShrink: 0 }} />}
      <span>{label}</span>
      {desc && !selected && <span style={{ color: FAINT, fontSize: '12px', fontWeight: 400 }} className="ml-1">— {desc}</span>}
    </button>
  );
}

function ProgressDots({ total, current }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className="rounded-full" style={{ width: i === current ? '22px' : '6px', height: '6px', background: i <= current ? BLUE : LINE, transition: 'all .3s' }} />
      ))}
    </div>
  );
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [searchParams] = useSearchParams();
  const isEdit = searchParams.get('edit') === '1';
  const [stepIdx, setStepIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [answers, setAnswers] = useState({
    country: null,
    vertical: null, vertical_other: '', workflow_types: [], communication_style: [],
    follow_up_timing: 'next_day', auto_create_tasks: true, execution_mode: 'draft',
  });

  // First-time only: pre-fill the country from the browser locale (still editable).
  useEffect(() => {
    if (isEdit) return;
    const guess = detectCountry();
    if (guess) setAnswers(p => (p.country ? p : { ...p, country: guess }));
  }, [isEdit]);

  // When editing preferences from Settings, drop first-time-only steps
  // (create-first-card + country, which is locked after first onboarding).
  const steps = isEdit ? STEPS.filter(s => s.id !== 'ready' && s.id !== 'country') : STEPS;
  const step = steps[stepIdx];
  const isLast = stepIdx === steps.length - 1;
  const workflowOptions = step.id === 'workflow' ? (step.options[answers.vertical] || step.options.general) : [];

  const canContinue = (() => {
    if (step.type === 'country') return !!answers.country;
    if (step.type === 'single') {
      if (answers[step.key] === null) return false;
      if (step.key === 'vertical' && answers.vertical === 'other') return !!answers.vertical_other.trim();
      return true;
    }
    if (step.type === 'multi') return answers.workflow_types.length > 0;
    if (step.type === 'multiTone') return (answers.communication_style || []).length > 0;
    return true;
  })();

  const handleSingle = (key, value) => setAnswers(p => ({ ...p, [key]: value }));  const handleMultiToggle = (value) => setAnswers(p => ({
    ...p,
    workflow_types: p.workflow_types.includes(value) ? p.workflow_types.filter(v => v !== value) : [...p.workflow_types, value],
  }));
  const handleToneToggle = (value) => setAnswers(p => {
    const cur = p.communication_style || [];
    return { ...p, communication_style: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] };
  });
  const handlePrefChange = (key, value) => setAnswers(p => ({ ...p, [key]: value }));

  const handleContinue = async () => {
    if (isLast) { if (isEdit) finishOnboarding('/settings'); return; }
    if (!isEdit && stepIdx === steps.length - 2) await saveProfile();
    setStepIdx(p => p + 1);
  };
  const handleSkip = () => { if (isLast) finishOnboarding(isEdit ? '/settings' : '/'); else setStepIdx(p => p + 1); };

  const saveProfile = async () => {
    try {
      await axios.post(`${API_BASE}/onboarding/complete`, {
        vertical: answers.vertical === 'other' ? (answers.vertical_other.trim() || 'general') : (answers.vertical || 'general'),
        workflow_types: answers.workflow_types,
        communication_style: (answers.communication_style && answers.communication_style.length) ? answers.communication_style : ['professional'],
        follow_up_timing: answers.follow_up_timing,
        auto_create_tasks: answers.auto_create_tasks,
        execution_mode: answers.execution_mode,
        ...(answers.country ? { country: answers.country } : {}),
      });
    } catch (err) {
      console.warn('Onboarding profile save failed (non-blocking):', err);
    }
  };

  const finishOnboarding = async (destination = '/') => {
    setSaving(true);
    await saveProfile();
    // Refresh in-memory auth state so ProtectedRoute doesn't bounce back to
    // /onboarding (the backend marks onboarding_completed=true on save).
    if (!isEdit) {
      updateUser({
        onboarding_completed: true,
        ...(answers.country ? { country: answers.country, home_region: regionForCountry(answers.country) } : {}),
      });
    }
    setSaving(false);
    navigate(destination);
  };

  const handleCTA = (value) => {
    if (value === 'paste' || value === 'upload') finishOnboarding('/capture');
    else if (value === 'meeting') finishOnboarding('/meetings');
    else finishOnboarding('/');
  };

  return (
    <div data-testid="onboarding-page" className="min-h-screen flex flex-col items-center justify-center px-4 py-12"
      style={{ background: '#F4F2EB', fontFamily: "'Work Sans', sans-serif" }}>
      <div className="mb-8">
        <img src={WORDMARK_BLUE} alt="Milk" style={{ height: '26px', width: 'auto' }} />
      </div>

      <div className="w-full rounded-3xl p-8 md:p-12"
        style={{ maxWidth: '600px', background: '#FFFFFF', border: `1px solid ${LINE}`, boxShadow: '0 1px 2px rgba(33,46,64,.04), 0 24px 50px rgba(33,46,64,.08)' }}>
        <p className="mb-4" style={{ color: FAINT, fontSize: '11px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
          {step.label} · {stepIdx + 1} of {steps.length}
        </p>

        <h1 className="mb-2" style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, fontSize: 'clamp(30px, 5vw, 46px)', lineHeight: 1.04, letterSpacing: '-0.3px', color: INK, whiteSpace: 'pre-line' }}>
          {step.heading}
        </h1>
        <p className="text-sm mb-8" style={{ color: MUTE }}>{step.sub}</p>

        {step.type === 'country' && (
          <div className="space-y-4" data-testid="onboarding-country-step">
            <Popover open={countryOpen} onOpenChange={setCountryOpen}>
              <PopoverTrigger asChild>
                <button
                  data-testid="onboarding-country-trigger"
                  className="w-full flex items-center justify-between rounded-2xl text-sm outline-none"
                  style={{ padding: '13px 18px', border: `1px solid ${LINE}`, background: '#FBFAF6', color: answers.country ? INK : FAINT }}
                >
                  <span className="flex items-center gap-2">
                    <MapPin size={15} style={{ color: BLUE_D }} />
                    {answers.country ? countryName(answers.country) : 'Select your country…'}
                  </span>
                  <ChevronsUpDown size={15} style={{ color: FAINT }} />
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0" style={{ width: 'var(--radix-popover-trigger-width)' }} align="start">
                <Command>
                  <CommandInput placeholder="Search country…" data-testid="onboarding-country-search" />
                  <CommandList>
                    <CommandEmpty>No country found.</CommandEmpty>
                    <CommandGroup>
                      {COUNTRIES.map(c => (
                        <CommandItem
                          key={c.code}
                          value={c.name}
                          data-testid={`onboarding-country-${c.code}`}
                          onSelect={() => { setAnswers(p => ({ ...p, country: c.code })); setCountryOpen(false); }}
                        >
                          <Check size={14} className="mr-2" style={{ opacity: answers.country === c.code ? 1 : 0 }} />
                          {c.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {answers.country && (
              <div className="rounded-2xl p-4" data-testid="onboarding-region-preview"
                style={{ background: 'rgba(105,148,198,0.06)', border: `1px solid rgba(105,148,198,0.2)` }}>
                <p style={{ color: FAINT, fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }} className="mb-1">
                  Your data will be stored in
                </p>
                <p className="text-sm font-bold" style={{ color: INK }}>
                  {REGION_LABELS[regionForCountry(answers.country)]}
                </p>
              </div>
            )}
          </div>
        )}

        {step.type === 'single' && (
          <div className="flex flex-wrap gap-2.5">
            {step.options.map(opt => (
              <Pill key={opt.value} label={opt.label} desc={opt.desc} selected={answers[step.key] === opt.value}
                onClick={() => handleSingle(step.key, opt.value)} testid={`onboarding-opt-${opt.value}`} />
            ))}
          </div>
        )}

        {step.type === 'single' && step.key === 'vertical' && answers.vertical === 'other' && (
          <input
            autoFocus
            data-testid="onboarding-vertical-other-input"
            value={answers.vertical_other}
            onChange={e => setAnswers(p => ({ ...p, vertical_other: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter' && canContinue) handleContinue(); }}
            placeholder="Type your role — e.g. Consultant, Recruiter, Lawyer…"
            className="w-full mt-3 rounded-2xl text-sm outline-none"
            style={{ padding: '13px 18px', border: `1px solid ${LINE}`, background: '#FBFAF6', color: INK }}
          />
        )}

        {step.type === 'multi' && (
          <div className="flex flex-wrap gap-2.5">
            {workflowOptions.map(opt => (
              <Pill key={opt} label={opt} selected={answers.workflow_types.includes(opt)} onClick={() => handleMultiToggle(opt)} />
            ))}
          </div>
        )}

        {step.type === 'multiTone' && (
          <div className="flex flex-wrap gap-2.5">
            {step.options.map(opt => (
              <Pill key={opt.value} label={opt.label} desc={opt.desc}
                selected={(answers.communication_style || []).includes(opt.value)}
                onClick={() => handleToneToggle(opt.value)} testid={`onboarding-opt-${opt.value}`} />
            ))}
          </div>
        )}

        {step.type === 'preferences' && (
          <div className="space-y-5">
            {step.subSteps.map(sub => (
              <div key={sub.key}>
                <p className="mb-2" style={{ color: FAINT, fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>{sub.question}</p>
                <div className="flex flex-wrap gap-2">
                  {sub.options.map(opt => (
                    <Pill key={String(opt.value)} label={opt.label} selected={answers[sub.key] === opt.value} onClick={() => handlePrefChange(sub.key, opt.value)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {step.type === 'cta' && (
          <div className="space-y-3">
            {step.options.map(opt => (
              <button key={opt.value} onClick={() => handleCTA(opt.value)} data-testid={`onboarding-cta-${opt.value}`}
                className="w-full flex items-center justify-between rounded-2xl border group"
                style={{ padding: '17px 20px', borderColor: LINE, background: '#FBFAF6', transition: 'border-color .15s, transform .15s, box-shadow .15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = BLUE; e.currentTarget.style.boxShadow = '0 8px 20px rgba(103,150,194,.14)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = LINE; e.currentTarget.style.boxShadow = 'none'; }}>
                <div className="text-left">
                  <p className="text-sm font-bold" style={{ color: INK }}>{opt.label}</p>
                  <p className="text-xs" style={{ color: MUTE }}>{opt.desc}</p>
                </div>
                <ArrowRight size={17} style={{ color: BLUE_D }} />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between w-full mt-6" style={{ maxWidth: '600px' }}>
        <ProgressDots total={steps.length} current={stepIdx} />
        {step.type !== 'cta' ? (
          <div className="flex items-center gap-3">
            {step.type !== 'country' && (
              <button onClick={handleSkip} className="text-sm px-4 py-2 rounded-full" style={{ color: MUTE, fontWeight: 600 }} data-testid="onboarding-skip">Skip</button>
            )}
            <button onClick={handleContinue} disabled={!canContinue || saving} data-testid="onboarding-continue"
              className="flex items-center gap-2 text-sm font-bold rounded-full"
              style={{ padding: '11px 24px', background: canContinue ? BLUE : '#E7E5DB', color: canContinue ? '#fff' : FAINT, cursor: canContinue ? 'pointer' : 'not-allowed', boxShadow: canContinue ? '0 8px 18px rgba(103,150,194,.30)' : 'none', transition: 'all .15s' }}>
              {saving ? 'Saving…' : (isLast && isEdit ? 'Save & finish' : 'Continue')}{!saving && <ArrowRight size={14} />}
            </button>
          </div>
        ) : (
          <button onClick={() => finishOnboarding('/')} className="text-sm px-4 py-2" style={{ color: MUTE, fontWeight: 600 }} data-testid="onboarding-skip">Skip for now</button>
        )}
      </div>
    </div>
  );
}
